// Vendor Program v2 PR 3 — confirm modal for sending draft VFI invoices
// (single row or batch selection). SP-4 WS-2: a draft whose contract check
// reports a VARIANCE can only be issued individually WITH a written reason
// (the server 400s without one; batch-issue fails such items per-item) —
// the difference is acknowledged, never silently accepted, and the amount
// is never auto-corrected. Single issues run through the single endpoint
// (carrying the reason); multi-selections run through POST batch-issue,
// which is per-item isolated.

import { useState } from "react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useBatchIssueInvoices,
  useIssueDraftInvoice,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import type { BatchIssueResultDto, VendorInvoiceDto } from "@/api/portfolioAdmin/types";
import { fmtMoney } from "../billing/vendorBillingFormat";
import { ContractCheckPanel } from "./ContractCheckPanel";

interface IssueDraftsConfirmModalProps {
  invoices: VendorInvoiceDto[];
  open: boolean;
  onClose: () => void;
  /** Called with the batch result after a successful POST (even when some
   *  items failed per-item) so the caller can prune its selection. */
  onIssued?: (result: BatchIssueResultDto) => void;
}

export function IssueDraftsConfirmModal({
  invoices,
  open,
  onClose,
  onIssued,
}: IssueDraftsConfirmModalProps) {
  const toast = useToast();
  const batchIssue = useBatchIssueInvoices();
  const singleIssue = useIssueDraftInvoice();
  const [ackReason, setAckReason] = useState("");

  const total = invoices.reduce((sum, i) => sum + i.amount, 0);
  const numberFor = (id: string) =>
    invoices.find((i) => i.id === id)?.invoiceNumber ?? id;

  // A single variance draft collects the ack reason here; in a batch the
  // variance rows fail per-item with a pointer to open them individually.
  const single = invoices.length === 1 ? invoices[0] : null;
  const singleVariance = single?.contractComparison?.status === "variance";
  const ackMissing = singleVariance && ackReason.trim() === "";
  const pending = batchIssue.isPending || singleIssue.isPending;

  const onConfirm = async () => {
    try {
      if (single) {
        const issued = await singleIssue.mutateAsync({
          invoiceId: single.id,
          varianceAckReason: singleVariance ? ackReason.trim() : null,
        });
        toast.success(`Invoice ${issued.invoiceNumber} issued.`);
        onIssued?.({ issued: [issued], failed: [] });
        onClose();
        return;
      }
      const result = await batchIssue.mutateAsync({
        invoiceIds: invoices.map((i) => i.id),
      });
      const summary = `${result.issued.length} issued; ${result.failed.length} failed.`;
      if (result.issued.length > 0) toast.success(summary);
      else toast.error(summary);
      if (result.failed.length > 0) {
        toast.error(
          result.failed.map((f) => `${numberFor(f.id)}: ${f.error}`).join(" · "),
        );
      }
      onIssued?.(result);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={
        invoices.length === 1
          ? `Issue invoice ${invoices[0].invoiceNumber}?`
          : `Issue ${invoices.length} invoices?`
      }
      description={
        single?.audience === "borrower"
          ? "Issuing sends this service-fee invoice to the borrower's portal and starts the due-date clock. This cannot be un-sent (void instead)."
          : "Issuing sends the invoice to the vendor portal and starts the due-date clock. This cannot be un-sent (void instead)."
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onConfirm()}
            disabled={pending || invoices.length === 0 || ackMissing}
            data-testid="issue-drafts-confirm"
          >
            {pending
              ? "Issuing…"
              : singleVariance
                ? "Issue with this reason"
                : "Issue"}
          </Button>
        </>
      }
    >
      <ul className="m-0 p-0 list-none flex flex-col gap-space-1" data-testid="issue-drafts-list">
        {invoices.map((i) => (
          <li
            key={i.id}
            className="flex items-baseline justify-between gap-space-2 text-small border-b border-border-light pb-space-1"
          >
            <span className="min-w-0 truncate">
              {i.invoiceNumber}
              <span className="text-caption text-gray-mid"> · {i.vendorName}</span>
            </span>
            <span className="text-right tabular-nums">{fmtMoney(i.amount)}</span>
          </li>
        ))}
      </ul>
      <p className="text-small m-0 mt-space-2 flex items-baseline justify-between gap-space-2">
        <span className="text-caption text-gray-mid uppercase tracking-label">Total</span>
        <span className="text-right tabular-nums font-display font-bold">
          {fmtMoney(total)}
        </span>
      </p>

      {singleVariance && single && (
        <div className="mt-space-3 flex flex-col gap-space-2" data-testid="variance-ack-block">
          <ContractCheckPanel
            comparison={single.contractComparison}
            vendorId={single.vendorId}
          />
          <label className="text-small font-medium text-onyx" htmlFor="variance-ack-reason">
            Why are you approving the difference?
          </label>
          <Textarea
            id="variance-ack-reason"
            rows={2}
            value={ackReason}
            onChange={(e) => setAckReason(e.target.value)}
            placeholder="e.g. one-off rush work agreed by phone on June 12"
            data-testid="variance-ack-input"
          />
          <p className="text-caption text-gray-mid m-0">
            Required — the reason is saved on the invoice for the audit trail.
            If the amount is simply wrong, send the report back instead.
          </p>
        </div>
      )}
      {!single &&
        invoices.some((i) => i.contractComparison?.status === "variance") && (
          <p
            className="text-small text-status-warning m-0 mt-space-2"
            data-testid="batch-variance-note"
          >
            Some selected drafts differ from their contract terms — those will
            not issue in a batch. Open each one to approve it with a reason.
          </p>
        )}
    </Modal>
  );
}
